import { HttpClient } from '@angular/common/http';
import { Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import
    {
        GetWalletsFromExternal,
        ICreateWalletError,
        ICreateWalletParams,
        ICreateWalletResponse,
        IWalletsFromExternal,
    } from '@common/api/crypto-wallet-public';
import { IWallet } from '@common/api/waallet';
import { eHDPath, hdPathGenerate } from '@common/hdPath';
import { SessionExternalService } from '@services/session-external/session-external.service';
import { ethers } from 'ethers';
import { BehaviorSubject } from 'rxjs';

@Component({
    selector: 'app-create-external',
    templateUrl: './create-external.component.html',
    styleUrls: [ './create-external.component.scss' ]
})
export class CreateExternalComponent
{
    public error$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
    private readonly DEFAULT_PLATFORM = 'EVM';
    private readonly DEFAULT_CHAIN = 1;

    private redirectUri: string;

    constructor(
        private sessionExternal: SessionExternalService,
        private route: ActivatedRoute,
        private http: HttpClient
    )
    {
        this.createWalletAutomatically();
    }

    public async createWalletAutomatically(): Promise<void>
    {
        try
        {
            this.error$.next(null);

            this.redirectUri = decodeURIComponent(this.route.snapshot.queryParams.redirect_uri);

            const createWalletResponse: ICreateWalletResponse = await this.processWalletRequest2();

            Object.keys(createWalletResponse).forEach(key =>
            {
                if (this.redirectUri.includes('?'))
                {
                    this.redirectUri += `&${ key }=${ encodeURIComponent(createWalletResponse[ key ]) }`;
                }
                else
                {
                    this.redirectUri += `?${ key }=${ encodeURIComponent(createWalletResponse[ key ]) }`;
                }
            });

            location.href = this.redirectUri;
        }
        catch (e)
        {
            console.error(e);
            this.error$.next(e.message);

            location.href = this.redirectUri + `&error=${ e.message }`;

            return;
        }
    }

    private async processWalletRequest(): Promise<ICreateWalletResponse>
    {
        const createBody: ICreateWalletParams = {
            access_token: decodeURIComponent(this.route.snapshot.queryParams.access_token),
            data_to_sign: decodeURIComponent(this.route.snapshot.queryParams.data_to_sign) || 'Data to sign',
            server_public_key: decodeURIComponent(this.route.snapshot.queryParams.server_public_key),
        };

        if (this.route.snapshot.queryParams.arrayify)
        {
            createBody[ 'arrayify' ] = this.route.snapshot.queryParams.arrayify;
        }

        const hidInfo: GetWalletsFromExternal = await this.sessionExternal.getWallets(createBody.access_token);

        if (!hidInfo)
        {
            return await this.createCryptoWalletWithDefault(createBody);
        }

        if (this.route.snapshot.queryParams[ 'mode' ] === '0')
        {
            return await this.getCwDataAndReturnWallet(createBody, hidInfo, true);
        }

        return await this.getCwDataAndReturnWallet(createBody, hidInfo);
    }

    private async processWalletRequest2(): Promise<ICreateWalletResponse>
    {
        const createBody: ICreateWalletParams = {
            access_token: decodeURIComponent(this.route.snapshot.queryParams.access_token),
            data_to_sign: decodeURIComponent(this.route.snapshot.queryParams.data_to_sign) || 'Data to sign',
            server_public_key: decodeURIComponent(this.route.snapshot.queryParams.server_public_key),
            mode: <0 | 1 | 2>Number(decodeURIComponent(this.route.snapshot.queryParams.mode) || 0),
        };

        const response = await this.http.post<ICreateWalletResponse | ICreateWalletError>(
            '/api/crypto-wallet-public/create-wallet-automatically',
            createBody
        ).toPromise();

        if (response[ 'error' ])
        {
            throw new Error(response[ 'error' ]);
        }

        return response as ICreateWalletResponse;
    }

    private async createCryptoWalletWithDefault(walletCreateParams: ICreateWalletParams): Promise<ICreateWalletResponse>
    {
        const [ sharedSecret, iv ] = await this.sessionExternal.initKeys(walletCreateParams.access_token, walletCreateParams.server_public_key);

        const phrase = this.generatePhrase();

        const ethersWallet = ethers.HDNodeWallet.fromPhrase(phrase.phrase, null);

        const key = await this.sessionExternal.getKey(sharedSecret, iv, walletCreateParams);

        const backupId = await this.sessionExternal.createBackup(
            { type: 'phrase', data: phrase.phrase },
            key,
            walletCreateParams.access_token,
            sharedSecret,
            iv
        );

        const signature = await ethersWallet.signMessage(walletCreateParams.data_to_sign);

        await this.sessionExternal.addCryptoWallet(
            walletCreateParams.access_token,
            {
                key_id: key.key_id,
                backup_id: backupId,
                wallet_address: ethersWallet.address,
            }
        );

        return {
            result: 0,
            wallet_address: ethersWallet.address,
            data_sign: signature,
            wallet_chain: this.DEFAULT_CHAIN.toString(),
            is_default: 1,
        };
    }

    private async getCwDataAndReturnWallet(walletCreateParams: ICreateWalletParams, hidInfo: IWalletsFromExternal, getDefault: boolean = false): Promise<ICreateWalletResponse>
    {
        const [ sharedSecret, iv ] = await this.sessionExternal.initKeys(walletCreateParams.access_token, walletCreateParams.server_public_key);

        const accountIndex = this.getAccountIndex(hidInfo.wallets, getDefault);
        const orderIndex = this.getOrderIndex(hidInfo.wallets);

        const hdPath = hdPathGenerate(eHDPath.BIP_44, accountIndex);

        const key = await this.sessionExternal.getKey(sharedSecret, iv, walletCreateParams, hidInfo.cryptowallet.key_id);
        const privateData = await this.sessionExternal.getBackup(hidInfo.cryptowallet.backup_id, key, walletCreateParams.access_token, sharedSecret, iv);

        const wallet = ethers.HDNodeWallet.fromPhrase(privateData.data, null, hdPath);

        const createWalletParams: { address: string, acc_index: number, hdPath: string, order_index: number; } = {
            address: wallet.address,
            acc_index: accountIndex,
            hdPath: hdPath,
            order_index: orderIndex
        };

        if (!getDefault)
        {
            await this.sessionExternal.addWallet(walletCreateParams.access_token, hidInfo.cryptowallet, createWalletParams);
        }

        const signature = await wallet.signMessage(walletCreateParams.data_to_sign);

        return {
            result: 0,
            wallet_address: wallet.address,
            data_sign: signature,
            wallet_chain: this.DEFAULT_CHAIN.toString(),
            is_default: getDefault ? 1 : 0,
        };
    }

    private getAccountIndex(wallets: IWallet[], getDefault: boolean = false): number
    {
        const walletIndexes = wallets.filter(item =>
        {
            return item.platformDb.name === this.DEFAULT_PLATFORM;
        }).map(item => item.account_index);

        const maxIndex = Math.max(...walletIndexes);
        let accountIndex = 0;
        if (getDefault) return accountIndex;

        maxIndex > 0 ?
            accountIndex = maxIndex + 1 :
            accountIndex = !walletIndexes.length ? 0 : walletIndexes.length;

        return accountIndex;
    }

    private getOrderIndex(wallets: IWallet[]): number
    {
        const filteredWallets = wallets.filter(item =>
        {
            return item.platformDb.name === this.DEFAULT_PLATFORM;
        });

        const walletsOrderIndexes = filteredWallets.map(item => item.order_index);

        const maxOrderIndex = Math.max(...walletsOrderIndexes);

        let orderIndex = 0;

        maxOrderIndex > 0 ?
            orderIndex = maxOrderIndex + 1 :
            orderIndex = !filteredWallets.length ? 0 : filteredWallets.length;

        return orderIndex;
    }

    private generatePhrase(): { phrase: string, address: string; }
    {
        const wallet = ethers.Wallet.createRandom();

        return {
            phrase: wallet.mnemonic.phrase,
            address: wallet.address
        };
    }
}
